import { deriveSrpPrivateKey, deriveSrpVerifier, generateSrpSalt } from '@/crypto/srp-client'
import { sessionStore } from '@features/auth/services/sessionStore'
import { verifyMasterPasswordMatchesSession } from '@features/auth/services/verifyMasterPassword'
import { ApiError } from '@services/apiClient'
import { EMAIL_RE } from '@shared/validation/emailPattern'
import { changeEmailInit } from '../api/changeEmailApi'

/**
 * メールアドレス変更フロー (T-H3)。
 *
 * 設計判断 (KEY_HIERARCHY.md §4.1 / CLAUDE.md §2):
 *   - SRP-x = H(srpSalt, username, password) なので email (= username) を変更すると
 *     verifier を再計算する必要がある。クライアントが新 email + 現マスターパスワードで
 *     新 srpSalt と verifier を生成して送信し、サーバはメール確認時に atomic に更新する。
 *   - 入力検証: 現マスターパスワードで `protectedSymmetricKey` を AES-GCM open し、
 *     現セッションの SK と一致するか確認する (= 完全クライアント側のパスワード検証)。
 *     不一致なら 'wrong_password' エラー → 早期 abort し、誤った verifier の送信を防ぐ。
 *   - 新 KDF パラメータは変更しない (sessionStore.material をそのまま使う)。
 *   - 成功時に session は変更しない (確認完了時にサーバが email を切替 → 次回 prelogin で
 *     新 email が使えるようになる)。
 */

export class ChangeEmailError extends Error {
  public readonly code:
    | 'no_session'
    | 'invalid_email'
    | 'same_email'
    | 'wrong_password'
    | 'email_taken'
    | 'too_many_requests'
    | 'network'
    | 'generic'

  public constructor(code: ChangeEmailError['code']) {
    super(code)
    this.code = code
    this.name = 'ChangeEmailError'
  }
}

export interface ChangeEmailInput {
  newEmail: string
  masterPassword: string
}

/**
 * メイン entry point。失敗時は ChangeEmailError を throw。
 */
export const changeEmailFlow = async (
  input: ChangeEmailInput,
): Promise<{ pendingEmail: string }> => {
  const newEmailRaw = input.newEmail.trim()
  if (!newEmailRaw || !EMAIL_RE.test(newEmailRaw)) {
    throw new ChangeEmailError('invalid_email')
  }

  const state = sessionStore.getState()
  if (state.kind !== 'unlocked') {
    throw new ChangeEmailError('no_session')
  }

  const newEmailLower = newEmailRaw.toLowerCase()
  if (newEmailLower === state.material.email.toLowerCase()) {
    throw new ChangeEmailError('same_email')
  }

  const ok = await verifyMasterPasswordMatchesSession(input.masterPassword)
  if (!ok) {
    throw new ChangeEmailError('wrong_password')
  }

  // 新 SRP material を導出 (新 email を username に組み込む)
  const newSrpSalt = generateSrpSalt()
  const newX = deriveSrpPrivateKey(newSrpSalt, newEmailLower, input.masterPassword)
  const newVerifier = deriveSrpVerifier(newX)

  try {
    const res = await changeEmailInit({
      newEmail: newEmailLower,
      srpSalt: newSrpSalt,
      srpVerifier: newVerifier,
    })

    return { pendingEmail: res.pendingEmail }
  } catch (err: unknown) {
    if (err instanceof ApiError) {
      if (err.status === 409) throw new ChangeEmailError('email_taken')
      if (err.status === 429) throw new ChangeEmailError('too_many_requests')
      throw new ChangeEmailError('network')
    }
    throw new ChangeEmailError('generic')
  }
}
